/**
 * Account Service
 *
 * CRUD operations for accounts.
 * All operations are local-first (IndexedDB).
 */

import { db, Account } from './db';
import { generateId } from '../utils/id';
import { syncManager } from './syncManager';
import { dataEvents } from './dataEvents';

// ============================================================
// Types
// ============================================================

export type AccountInput = Omit<
  Account,
  'id' | 'createdAt' | 'updatedAt' | 'syncStatus' | 'balance'
>;

// ============================================================
// Debug Logging
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[ACCT]', ...args);
  }
}

// ============================================================
// CRUD Operations
// ============================================================

export const accountService = {
  /**
   * Create a new account.
   * Validates required fields before creation.
   */
  async create(data: AccountInput): Promise<string> {
    // Validate required fields
    if (!data.name || data.name.trim() === '') {
      throw new Error('Account name is required');
    }

    // Validate account type
    const validTypes = ['checking', 'savings', 'cash', 'credit', 'crypto', 'investment'];
    if (!validTypes.includes(data.type)) {
      throw new Error(`Invalid account type: ${data.type}. Must be one of: ${validTypes.join(', ')}`);
    }

    // Validate currency (basic check - 3 uppercase letters)
    if (!data.currency || !/^[A-Z]{3}$/.test(data.currency)) {
      throw new Error('Currency must be a valid 3-letter code (e.g., USD, EUR, THB)');
    }

    // Trim name for storage
    const trimmedName = data.name.trim();

    // Check for duplicate name (case-insensitive)
    const existing = await this.getByName(trimmedName);
    if (existing) {
      throw new Error(`Account "${trimmedName}" already exists`);
    }

    const id = generateId();
    const now = new Date();

    const account: Account = {
      ...data,
      name: trimmedName, // Use trimmed name
      id,
      balance: 0,
      createdAt: now,
      updatedAt: now,
      syncStatus: 'pending',
    };

    await db.accounts.add(account);
    log('Created account:', id, trimmedName);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('accounts');

    return id;
  },

  /**
   * Get an account by ID.
   */
  async getById(id: string): Promise<Account | undefined> {
    return db.accounts.get(id);
  },

  /**
   * Get an account by name (case-insensitive).
   * BUG-017 FIX: Changed from equals() to equalsIgnoreCase() to match category behavior.
   */
  async getByName(name: string): Promise<Account | undefined> {
    return db.accounts.where('name').equalsIgnoreCase(name).first();
  },

  /**
   * Get all accounts.
   */
  async getAll(): Promise<Account[]> {
    return db.accounts.toArray();
  },

  /**
   * Get active accounts only, sorted by sortOrder.
   */
  async getActive(): Promise<Account[]> {
    // IndexedDB doesn't support boolean indexes well - use filter instead
    const accounts = await db.accounts.filter((a) => a.isActive === true).toArray();
    // Sort by sortOrder (lower first), undefined sortOrder goes to end
    return accounts.sort((a, b) => {
      const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  },

  /**
   * Get hidden (inactive) accounts only, sorted by name.
   */
  async getHidden(): Promise<Account[]> {
    const accounts = await db.accounts.filter((a) => a.isActive === false).toArray();
    return accounts.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Update an account.
   */
  async update(
    id: string,
    data: Partial<Omit<Account, 'id' | 'createdAt'>>
  ): Promise<void> {
    await db.accounts.update(id, {
      ...data,
      updatedAt: new Date(),
      syncStatus: 'pending',
    });
    log('Updated account:', id);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('accounts');
  },

  /**
   * Delete an account.
   * Note: Consider soft-delete (isActive: false) instead.
   *
   * Validates that no transactions or scheduled transactions reference this account.
   * Returns { success: false, transactionCount, scheduleCount } if account has references.
   */
  async delete(id: string): Promise<{ success: boolean; transactionCount?: number; scheduleCount?: number }> {
    // Check if account has transactions (as source)
    const transactions = await db.transactions
      .where('accountId')
      .equals(id)
      .filter(t => !t.deletedAt)
      .toArray();

    // Also check if account is destination of any transfers
    const transfersTo = await db.transactions
      .where('toAccountId')
      .equals(id)
      .filter(t => !t.deletedAt)
      .toArray();

    const totalTxns = transactions.length + transfersTo.length;

    if (totalTxns > 0) {
      log('Cannot delete account - has', totalTxns, 'transactions');
      return { success: false, transactionCount: totalTxns };
    }

    // Check if account has scheduled transactions (as source)
    const schedules = await db.scheduledTransactions
      .where('accountId')
      .equals(id)
      .filter(s => !s.deletedAt && !s.isIgnoredPermanently)
      .toArray();

    // Also check if account is destination of any scheduled transfers
    const schedulesTo = await db.scheduledTransactions
      .filter(s => s.toAccountId === id && !s.deletedAt && !s.isIgnoredPermanently)
      .toArray();

    const totalSchedules = schedules.length + schedulesTo.length;

    if (totalSchedules > 0) {
      log('Cannot delete account - has', totalSchedules, 'scheduled transactions');
      return { success: false, scheduleCount: totalSchedules };
    }

    // Safe to delete
    await db.accounts.delete(id);
    log('Deleted account:', id);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('accounts');

    return { success: true };
  },

  /**
   * Soft-delete an account (deactivate).
   */
  async deactivate(id: string): Promise<void> {
    await this.update(id, { isActive: false });
    log('Deactivated account:', id);
  },

  /**
   * Reactivate an account.
   */
  async activate(id: string): Promise<void> {
    await this.update(id, { isActive: true });
    log('Activated account:', id);
  },

  /**
   * Calculate account balance from transactions.
   * BUG-058 FIX: Now includes initialBalance to preserve imported starting balances.
   *
   * IMPORTANT: Filters out soft-deleted transactions (deletedAt set)
   * to prevent double-counting after delete + recalculate.
   *
   * Formula: balance = initialBalance + sum(transactions)
   */
  async calculateBalance(accountId: string): Promise<number> {
    // BUG-058 FIX: Get initial balance (starting balance not from transactions)
    const account = await db.accounts.get(accountId);
    const initialBalance = account?.initialBalance ?? 0;

    const allTransactions = await db.transactions
      .where('accountId')
      .equals(accountId)
      .toArray();

    // Also get transfers TO this account
    const allIncoming = await db.transactions
      .where('toAccountId')
      .equals(accountId)
      .toArray();

    // CRITICAL: Filter out soft-deleted transactions
    // Without this filter, deleted transactions get counted twice:
    // once when deleted (balance reversed) and again in recalculate
    const transactions = allTransactions.filter((t) => !t.deletedAt);
    const incomingTransfers = allIncoming.filter((t) => !t.deletedAt);

    // Start with initial balance (BUG-058 FIX)
    let balance = initialBalance;

    for (const t of transactions) {
      if (t.type === 'income') {
        balance += t.amount;
      } else if (t.type === 'expense') {
        balance -= t.amount;
      } else if (t.type === 'transfer') {
        // Outgoing transfer
        balance -= t.amount;
      }
    }

    // Add incoming transfers
    for (const t of incomingTransfers) {
      balance += t.amount;
    }

    return balance;
  },

  /**
   * Update all account balances.
   */
  async recalculateAllBalances(): Promise<void> {
    const accounts = await this.getAll();

    for (const account of accounts) {
      // BUG-064 FIX: Guard against undefined id
      if (!account?.id) {
        console.warn('[ACCOUNTS] recalculateAllBalances: account missing id', account);
        continue;
      }
      const balance = await this.calculateBalance(account.id);
      await db.accounts.update(account.id, { balance });
    }

    log('Recalculated balances for', accounts.length, 'accounts');
  },

  /**
   * Get total balance across all accounts.
   */
  async getTotalBalance(): Promise<number> {
    const accounts = await this.getActive();
    return accounts.reduce((sum, a) => sum + a.balance, 0);
  },

  /**
   * Get accounts grouped by type.
   */
  async getGroupedByType(): Promise<Map<Account['type'], Account[]>> {
    const accounts = await this.getActive();
    const grouped = new Map<Account['type'], Account[]>();

    for (const account of accounts) {
      const list = grouped.get(account.type) || [];
      list.push(account);
      grouped.set(account.type, list);
    }

    return grouped;
  },

  /**
   * Get pending sync accounts.
   */
  async getPendingSync(): Promise<Account[]> {
    return db.accounts.where('syncStatus').equals('pending').toArray();
  },

  /**
   * Mark accounts as synced.
   */
  async markSynced(ids: string[]): Promise<void> {
    await db.accounts.where('id').anyOf(ids).modify({ syncStatus: 'synced' });
    log('Marked', ids.length, 'accounts as synced');
  },

  /**
   * Get account count.
   */
  async count(): Promise<number> {
    return db.accounts.count();
  },

  /**
   * Update account sort order.
   * @param orderedIds - Array of account IDs in desired display order
   */
  async updateOrder(orderedIds: string[]): Promise<void> {
    await db.transaction('rw', db.accounts, async () => {
      for (let i = 0; i < orderedIds.length; i++) {
        await db.accounts.update(orderedIds[i], {
          sortOrder: i,
          updatedAt: new Date(),
        });
      }
    });
    log('Updated account order for', orderedIds.length, 'accounts');

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('accounts');
  },

  /**
   * Reconcile account balances after sync.
   * BUG-056 FIX: Compares stored balance to calculated balance from transactions.
   * If they differ, recalculates from transactions to ensure consistency.
   *
   * This handles the case where two devices create transactions concurrently:
   * - Both sync their account balances (last-write-wins)
   * - Both get each other's transactions
   * - But the "winning" balance might not account for all transactions
   *
   * By recalculating from transactions, we ensure balance is always correct.
   *
   * @param accountIds - Optional list of account IDs to check (default: all)
   * @returns Number of accounts that were corrected
   */
  async reconcileBalancesAfterSync(accountIds?: string[]): Promise<number> {
    const accounts = accountIds
      ? await Promise.all(accountIds.map((id) => db.accounts.get(id)))
      : await this.getAll();

    let correctedCount = 0;

    for (const account of accounts) {
      if (!account || !account.id) continue;

      // Check if account has any transactions (excluding soft-deleted)
      const txCount = await db.transactions
        .where('accountId')
        .equals(account.id)
        .filter((t) => !t.deletedAt)
        .count();

      // Also check incoming transfers
      const incomingCount = await db.transactions
        .where('toAccountId')
        .equals(account.id)
        .filter((t) => !t.deletedAt)
        .count();

      const hasTransactions = txCount > 0 || incomingCount > 0;

      // Only reconcile if account has transactions
      // If no transactions, trust the stored balance (could be from import or server)
      if (!hasTransactions) {
        continue;
      }

      const calculatedBalance = await this.calculateBalance(account.id);
      const storedBalance = account.balance;
      const discrepancy = Math.abs(calculatedBalance - storedBalance);

      // Allow tiny floating point differences (0.01 threshold)
      if (discrepancy > 0.01) {
        log(
          '[RECONCILE] Balance discrepancy detected for',
          account.name,
          ': stored=',
          storedBalance,
          ', calculated=',
          calculatedBalance,
          ', diff=',
          discrepancy
        );

        // Fix by recalculating from transactions
        // Don't trigger sync push - this is a local correction, not a user change
        // The next regular sync will push if needed
        await db.accounts.update(account.id, {
          balance: calculatedBalance,
          updatedAt: new Date(),
          syncStatus: 'pending',
        });

        log('[RECONCILE] Fixed', account.name, ': ', storedBalance, '->', calculatedBalance);
        correctedCount++;
      }
    }

    if (correctedCount > 0) {
      log('[RECONCILE] Corrected', correctedCount, 'account balances');
      // Notify UI to refresh with corrected balances
      dataEvents.emit('accounts');

      // BUG-059 FIX: Trigger sync to push corrected balances to server
      // Without this, corrected balance might not propagate until next user-initiated change
      syncManager.triggerPush();
    }

    return correctedCount;
  },

  /**
   * Audit all account balances for discrepancies.
   * Returns detailed results without making changes.
   * Use reconcileBalancesAfterSync() to fix issues.
   */
  async auditBalances(): Promise<{
    account: string;
    accountId: string;
    storedBalance: number;
    calculatedBalance: number;
    discrepancy: number;
  }[]> {
    const accounts = await this.getAll();
    const results: {
      account: string;
      accountId: string;
      storedBalance: number;
      calculatedBalance: number;
      discrepancy: number;
    }[] = [];

    for (const account of accounts) {
      if (!account.id) continue;

      const calculatedBalance = await this.calculateBalance(account.id);
      const discrepancy = Math.abs(calculatedBalance - account.balance);

      if (discrepancy > 0.01) {
        results.push({
          account: account.name,
          accountId: account.id,
          storedBalance: account.balance,
          calculatedBalance,
          discrepancy,
        });
      }
    }

    return results;
  },
};
